import React from 'react';

import { makeStyles } from '@material-ui/core/styles';

import { Translate } from '@hydra/common/components';
import { CriteriumSkeleton } from '@hydra/common/criterium';
import { useReviewContext } from '@hydra/common/context';

const googlePreview = {
  desktopTitle: '#1a0dab',
  desktopDescription: '#545454',
  mobileTitle: '#1967d2',
  mobileDescription: '#3c4043'
};

const useStyles = makeStyles(theme => ({
  googlePreviewSection: {
    breakInside: 'avoid'
  },
  googlePreviewDesktop: {
    fontFamily: 'arial',
    maxWidth: 600,
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(2)
  },
  googlePreviewDesktopTitle: {
    color: googlePreview.desktopTitle,
    lineHeight: '1.5em',
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.fontSizeBase
  },
  googlePreviewDesktopUrl: {
    fontSize: theme.typography.fontSizeSmall
  },
  googlePreviewDesktopDescription: {
    color: googlePreview.desktopDescription,
    lineHeight: '1.25rem',
    fontSize: theme.typography.fontSizeSmall
  },
  googlePreviewMobile: {
    width: 342,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2, 3),
    fontFamily: 'arial',
    boxShadow: 'rgba(32, 33, 36, 0.28) 0 1px 6px',
    borderRadius: theme.shape.borderRadius * 4,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      maxWidth: 342
    }
  },
  googlePreviewMobileUrl: {
    marginLeft: theme.spacing(1.5),
    fontSize: 12,
    display: 'inline',
    wordBreak: 'break-all'
  },
  googlePreviewMobileTitle: {
    padding: theme.spacing(0.75, 0),
    color: googlePreview.mobileTitle,
    lineHeight: '1.5rem',
    fontSize: theme.typography.fontSizeBase
  },
  googlePreviewMobileDescription: {
    color: googlePreview.mobileDescription,
    lineHeight: '1.25rem',
    fontSize: theme.typography.fontSizeSmall
  }
}));

interface GooglePreviewProps {
  title: string
  description: string
  url: string
}

interface CriteriumGooglePreviewProps {
  desktop: GooglePreviewProps
  mobile: GooglePreviewProps
  missing: boolean
}

export default function CriteriumGooglePreview ({ desktop, mobile, missing }: CriteriumGooglePreviewProps) {
  const classes = useStyles();
  const { variant } = useReviewContext();

  return missing
    ? (variant === 'nocrit' ? <CriteriumSkeleton width='20%' /> : <Translate content='missing' />)
    : (
      <>
        <div className={classes.googlePreviewSection}>
          <Translate content='google_preview_desktop_version' />
          <div className={classes.googlePreviewDesktop}>
            <div className={classes.googlePreviewDesktopUrl}>{variant === 'nocrit' ? <CriteriumSkeleton width='20%' /> : desktop.url}</div>
            <div className={classes.googlePreviewDesktopTitle}>{variant === 'nocrit' ? <CriteriumSkeleton width='40%' style={{ background: '#1A67D2' }} /> : desktop.title}</div>
            <div className={classes.googlePreviewDesktopDescription}>{variant === 'nocrit' ? <><CriteriumSkeleton width='80%' /><CriteriumSkeleton width='30%' /></> : desktop.description}</div>
          </div>
        </div>
        <div className={classes.googlePreviewSection}>
          <Translate content='google_preview_mobile_version' />
          <div className={classes.googlePreviewMobile}>
            <img src={`https://www.google.com/s2/favicons?sz=16&domain=${(mobile.url || '').split(' ')[0]}`} alt='Favicon' />
            <div className={classes.googlePreviewMobileUrl}>{mobile.url}</div>
            <div className={classes.googlePreviewMobileTitle}>{variant === 'nocrit' ? <CriteriumSkeleton style={{ background: '#1A67D2' }} width='75%' /> : mobile.title}</div>
            <div className={classes.googlePreviewMobileDescription}>{variant === 'nocrit' ? <><CriteriumSkeleton /><CriteriumSkeleton width='90%' /><CriteriumSkeleton width='95%' /><CriteriumSkeleton width='65%' /></> : mobile.description}</div>
          </div>
        </div>
      </>
      );
}
